import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CloudArrowUp,
  FilePdf,
  LinkSimple,
  Plus,
  Trash,
  UploadSimple,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const CATS = ["Warranty", "Care & maintenance", "Technical data", "Installation", "Certificate", "Brochure", "Other"];
const cls = "h-10 border-zinc-800 bg-zinc-950 text-sm";

const Row = ({ item, onChange, onDelete, onUpload, onRemoveFile, makers = [] }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const patch = (k, v) => onChange({ ...item, [k]: v });
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      await onUpload(item.id, f);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4"
      data-testid={`site-resource-${item.id}`}
    >
      <div className="grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto]">
        <Input
          data-testid={`site-resource-${item.id}-title`}
          value={item.title}
          onChange={(e) => patch("title", e.target.value)}
          placeholder="Document title"
          className={cls}
        />
        <Input
          data-testid={`site-resource-${item.id}-manufacturer`}
          value={item.manufacturer}
          onChange={(e) => patch("manufacturer", e.target.value)}
          placeholder="Manufacturer (e.g. Cambria)"
          list="site-resource-makers"
          className={cls}
        />
        <select
          data-testid={`site-resource-${item.id}-category`}
          value={item.category}
          onChange={(e) => patch("category", e.target.value)}
          className="h-10 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
        >
          {CATS.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          <Switch
            data-testid={`site-resource-${item.id}-published`}
            checked={item.published !== false}
            onCheckedChange={(v) => patch("published", v)}
          />
          Live
        </label>
      </div>
      <Textarea
        data-testid={`site-resource-${item.id}-description`}
        rows={2}
        value={item.description || ""}
        onChange={(e) => patch("description", e.target.value)}
        placeholder="One line on what's inside (optional)"
        className="mt-3 border-zinc-800 bg-zinc-950 text-sm"
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.file_name ? (
          <span
            className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-200"
            data-testid={`site-resource-${item.id}-file`}
          >
            <FilePdf size={14} /> {item.file_name}
            <button
              type="button"
              data-testid={`site-resource-${item.id}-remove-file`}
              onClick={() => onRemoveFile(item.id)}
              className="text-emerald-200/60 hover:text-red-300"
              title="Remove file"
            >
              ×
            </button>
          </span>
        ) : (
          <span className="flex items-center gap-2 text-xs text-zinc-500">
            <LinkSimple size={14} />
            <Input
              data-testid={`site-resource-${item.id}-link`}
              value={item.link || ""}
              onChange={(e) => patch("link", e.target.value)}
              placeholder="…or paste the manufacturer's URL"
              className="h-8 w-72 border-zinc-800 bg-zinc-950 text-xs"
            />
          </span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.png,.jpg,.jpeg"
          className="hidden"
          data-testid={`site-resource-${item.id}-file-input`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`site-resource-${item.id}-upload`}
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="h-8 border-zinc-700 text-xs"
        >
          <UploadSimple size={14} className="mr-1" /> {busy ? "Uploading…" : item.file_name ? "Replace file" : "Upload file"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid={`site-resource-${item.id}-delete`}
          onClick={() => onDelete(item.id)}
          className="ml-auto h-8 text-xs text-zinc-500 hover:text-red-300"
        >
          <Trash size={14} className="mr-1" /> Remove
        </Button>
      </div>
    </div>
  );
};

// Website settings → Company pages → Resources: the document library behind /resources
export const ResourcesEditor = () => {
  const [items, setItems] = useState([]);
  const [makers, setMakers] = useState([]);
  const [dirty, setDirty] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const dropRef = useRef(null);

  const load = () =>
    api
      .get("/settings/site/resources")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {});
  useEffect(() => {
    load();
    api
      .get("/settings/site/resources/manufacturers")
      .then(({ data }) => setMakers(data.names || []))
      .catch(() => {});
  }, []);

  const replace = (row) => setItems((list) => list.map((r) => (r.id === row.id ? row : r)));

  const change = (row) => {
    replace(row);
    setDirty((d) => ({ ...d, [row.id]: true }));
  };

  const saveRow = async (row) => {
    try {
      const { data } = await api.put(`/settings/site/resources/${row.id}`, {
        title: row.title,
        manufacturer: row.manufacturer,
        category: row.category,
        description: row.description || "",
        link: row.link || "",
        published: row.published !== false,
        sort: row.sort || 0,
      });
      replace(data);
      setDirty((d) => ({ ...d, [row.id]: false }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveAll = async () => {
    const rows = items.filter((r) => dirty[r.id]);
    await Promise.all(rows.map(saveRow));
    toast.success(rows.length ? `Saved ${rows.length} document${rows.length === 1 ? "" : "s"}` : "Nothing to save");
  };

  const addBlank = async () => {
    try {
      const { data } = await api.post("/settings/site/resources", {
        title: "New document",
        manufacturer: "",
        category: "Warranty",
        description: "",
        link: "",
        published: false,
      });
      setItems((list) => [data, ...list]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const uploadTo = async (id, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/settings/site/resources/${id}/file`, form);
      replace(data);
      toast.success(`Attached ${file.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeFile = async (id) => {
    try {
      const { data } = await api.delete(`/settings/site/resources/${id}/file`);
      replace(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this document from the website?")) return;
    try {
      await api.delete(`/settings/site/resources/${id}`);
      setItems((list) => list.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const quickAdd = async (files) => {
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const { data } = await api.post("/settings/site/resources/quick", form);
        setItems((list) => [data, ...list]);
        if (data.matched_manufacturer) {
          toast.success(`Added ${data.title}`, {
            description: `Manufacturer set to ${data.matched_manufacturer} from the file name — change it below if that's wrong.`,
          });
        } else {
          toast.success(`Added ${data.title}`, {
            description: data.manufacturer ? undefined : "Couldn't tell the manufacturer from the file name — pick one below.",
          });
        }
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  };

  const pending = Object.values(dirty).filter(Boolean).length;

  return (
    <div data-testid="site-resources-editor">
      <div
        data-testid="site-resources-drop"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          quickAdd([...e.dataTransfer.files]);
        }}
        onClick={() => dropRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-8 text-center transition-colors duration-150 ${
          dragOver ? "border-yellow-500 bg-yellow-500/10" : "border-zinc-700 bg-zinc-950/40 hover:border-zinc-500"
        }`}
      >
        <CloudArrowUp size={26} className="text-yellow-500" />
        <div className="text-sm text-zinc-200">Drop warranty PDFs here, or click to choose</div>
        <div className="text-xs text-zinc-500">
          Each file becomes a live document named after the file — the manufacturer is guessed from the
          file name (e.g. <span className="text-zinc-300">Cambria-Warranty.pdf</span>) against your brands.
        </div>
        <input
          ref={dropRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.png,.jpg,.jpeg"
          className="hidden"
          data-testid="site-resources-quick-input"
          onChange={(e) => {
            quickAdd([...(e.target.files || [])]);
            e.target.value = "";
          }}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="site-resources-add"
          onClick={addBlank}
          className="h-9 border-zinc-700 text-xs"
        >
          <Plus size={14} className="mr-1" /> Add a link instead
        </Button>
        <Button
          type="button"
          size="sm"
          data-testid="site-resources-save"
          onClick={saveAll}
          disabled={!pending}
          className="h-9 bg-yellow-500 text-xs font-bold text-black hover:bg-yellow-400 disabled:opacity-40"
        >
          Save changes{pending ? ` (${pending})` : ""}
        </Button>
        <span className="text-xs text-zinc-500" data-testid="site-resources-count">
          {items.length} document{items.length === 1 ? "" : "s"} ·{" "}
          {items.filter((r) => r.published !== false && (r.file_name || r.link)).length} live on /resources
        </span>
      </div>

      <div className="mt-4 space-y-3" data-testid="site-resources-list">
        {items.map((r) => (
          <Row
            key={r.id}
            item={r}
            makers={makers}
            onChange={change}
            onDelete={del}
            onUpload={uploadTo}
            onRemoveFile={removeFile}
          />
        ))}
        <datalist id="site-resource-makers">
          {makers.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        {!items.length && (
          <div className="text-sm text-zinc-500" data-testid="site-resources-empty">
            No documents yet — drop the first warranty PDF above.
          </div>
        )}
      </div>
    </div>
  );
};
